import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { VirtuosoGrid } from 'react-virtuoso';
// Context
import { useFiltersContext } from 'components/common3/PageWithFiltersWrapper/PageWithFiltersContext';
// Api
import { GET_STORES } from 'api/store/queries';
// Types
import { UserRole } from 'api/graphql-global-types';
import {
  GetStores,
  GetStoresVariables,
  GetStores_stores_entities,
} from 'api/store/types/GetStores';
import { SearchLocation } from 'api/graphql-analytics-global-types';
// Components
import InfiniteScrollLoader from 'ui3/InfiniteScrollLoader/InfiniteScrollLoader';
import StoreListItem from 'components/common3/StoresList/components/StoreListItem/StoreListItem';
import NotFound from 'components/common/NotFound/NotFound';
import PageWithFiltersWrapper from 'components/common3/PageWithFiltersWrapper/PageWithFiltersWrapper';
// Styles
import styles from './Creators.module.scss';

const PER_PAGE = 20;

const VirtuosoGridItem: React.FC<{
  item: GetStores_stores_entities;
}> = React.memo(({ item }) => {
  return <StoreListItem store={item} type="list" />;
});
VirtuosoGridItem.displayName = 'VirtuosoGridItem';

export const Creators = () => {
  const { filters, sorting, searchKey, search } = useFiltersContext();

  const inputVariables = useMemo(() => {
    let storeRoles = [UserRole.Organization, UserRole.ContentCreator];

    if (
      filters.isOrganization?.[0] === 'true' &&
      filters.isContentCreator?.[0] === 'true'
    ) {
      storeRoles = [UserRole.Organization, UserRole.ContentCreator];
    } else if (filters.isOrganization?.[0] === 'true') {
      storeRoles = [UserRole.Organization];
    } else if (filters.isContentCreator?.[0] === 'true') {
      storeRoles = [UserRole.ContentCreator];
    }

    return {
      input: {
        orderBy: sorting.order,
        direction: sorting.direction,
        limit: PER_PAGE,
        offset: 0,
        storeIds: [
          ...filters.athleteIds,
          ...filters.organizationIds,
          ...filters.contentCreatorIds,
        ],
        ...(filters?.sports?.length
          ? { sports: filters.sports.map((i) => Number(i)) }
          : {}),
        ...(filters?.hashtagIds?.length
          ? { hashtagIds: filters.hashtagIds.map((i) => Number(i)) }
          : {}),
        ...(filters.isStreamer?.[0] === 'true' ? { isStreamer: true } : {}),
        ...(filters.isShoppingEnabled?.[0] === 'true'
          ? { isShoppingEnabled: true }
          : {}),
        ...(searchKey &&
          search[searchKey] && {
            [searchKey]: search[searchKey],
          }),
      },
      storeRoles,
    };
  }, [filters, sorting, searchKey, search]);

  const { data, loading, fetchMore } = useQuery<GetStores, GetStoresVariables>(
    GET_STORES,
    {
      variables: {
        storeRoles: inputVariables.storeRoles,
        input: {
          ...inputVariables.input,
        },
        withFilters: true,
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  const allFilters = {
    ...data?.stores.filters,
    ...{ isStreamer: filters.isStreamer?.[0] === 'true' ? true : false },
    ...{
      isShoppingEnabled:
        filters.isShoppingEnabled?.[0] === 'true' ? true : false,
    },
  };

  const fetchMoreData = () => {
    const prevOffset = data?.stores?.offset || 0;
    const offset = prevOffset ? prevOffset + PER_PAGE : PER_PAGE;

    fetchMore({
      variables: {
        storeRoles: inputVariables.storeRoles,
        input: {
          ...inputVariables.input,
          offset,
        },
      },
    });
  };

  const handleOnEndReached = () => {
    if (hasMore) {
      fetchMoreData();
    }
  };

  const organizations = data?.stores?.entities || [];
  const total = data?.stores?.total;
  const hasMore = data ? organizations.length !== total : false;
  const isNotFound = !loading && total === 0;

  return (
    <PageWithFiltersWrapper
      title="Creators"
      sortKey="Organizations"
      loading={loading}
      filters={allFilters}
      searchBarLocation={SearchLocation.Organizations}
      headerSpaceForMobile={false}
    >
      {isNotFound && <NotFound title="Stores not found" />}

      {!!organizations.length && (
        <VirtuosoGrid
          useWindowScroll
          overscan={PER_PAGE * 5}
          data={organizations}
          listClassName={styles.root}
          totalCount={data?.stores.total}
          itemContent={(index, item) => <VirtuosoGridItem item={item} />}
          endReached={handleOnEndReached}
          components={{
            Footer: () => {
              if (!hasMore) {
                return null;
              }

              return <InfiniteScrollLoader />;
            },
          }}
        />
      )}
    </PageWithFiltersWrapper>
  );
};

export default Creators;
